export const areArraysEqual = <T = string>(arr1: T[], arr2: T[]): boolean => {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const set = new Set(arr1);
  return arr2.every((item) => set.has(item));
};

type DropdownOption = { value: string; label: string };

type ColumnSettingsSource = {
  settings?: { labels?: unknown } | null;
  settings_str?: string | null;
  settingsStr?: string | null;
};

/**
 * Monday API 2025-10+ returns status labels as an array of objects on `settings`.
 * Older API versions used a string map on `settings_str` (`{ "0": "Done" }`).
 */
const normalizeStatusLabels = (labels: unknown): DropdownOption[] => {
  if (!labels) {
    return [];
  }

  if (Array.isArray(labels)) {
    return labels
      .filter(
        (item): item is { label?: unknown; is_deactivated?: boolean } =>
          !!item && typeof item === 'object' && !Array.isArray(item),
      )
      .filter((item) => !item.is_deactivated)
      .map((item) => {
        const text = typeof item.label === 'string' ? item.label : '';
        return { value: text, label: text };
      })
      .filter((option) => option.label);
  }

  if (typeof labels === 'object') {
    return Object.values(labels as Record<string, unknown>)
      .filter((value): value is string => typeof value === 'string' && !!value)
      .map((text) => ({ value: text, label: text }));
  }

  return [];
};

const resolveColumnSettings = (
  columnMeta: ColumnSettingsSource | undefined,
): { labels?: unknown } => {
  if (
    columnMeta?.settings &&
    typeof columnMeta.settings === 'object' &&
    columnMeta.settings.labels != null
  ) {
    return columnMeta.settings;
  }

  const settingsStr = columnMeta?.settings_str ?? columnMeta?.settingsStr;
  try {
    return JSON.parse(settingsStr || '{}') as { labels?: unknown };
  } catch {
    return {};
  }
};

/**
 * Resolve dropdown options from a Monday status column.
 * Accepts either `column_values` (nested `column.settings`) or `board.columns`
 * (flat `settings` / `settings_str`).
 */
export const getDropdownLabels = ({
  boardColumns,
  targetColumnId,
}: {
  boardColumns: Array<{ id: string; column?: ColumnSettingsSource } & ColumnSettingsSource>;
  targetColumnId: string;
}): DropdownOption[] => {
  if (!boardColumns?.length || !targetColumnId) {
    return [];
  }

  const columnObject = boardColumns.find((col) => col.id === targetColumnId);
  if (!columnObject) {
    return [];
  }

  // column_values shape nests settings under `.column`; board.columns is flat
  const columnMeta = columnObject.column ?? columnObject;
  const settings = resolveColumnSettings(columnMeta);

  return normalizeStatusLabels(settings?.labels);
};

export const isValidUrl = (url: string): boolean => {
  const urlPattern = /^(https?:\/\/|www\.)[\da-z.-]+\.[a-z.]{2,6}[/?#\S]*$/i;
  return urlPattern.test(url);
};

export const safeCloseWindow = (): void => {
  try {
    // Detach opener if any to avoid cross-origin restrictions
    (window as any).opener = null;
  } catch (_) {}

  try {
    // Safari/iOS hack to allow closing a non-script-opened tab
    window.open('', '_self');
  } catch (_) {}

  try {
    window.close();
  } catch (_) {}

  if (!window.closed) {
    try {
      if (document.referrer) {
        window.location.replace(document.referrer);
        return;
      }
    } catch (_) {}

    try {
      if (window.history.length > 1) {
        window.history.back();
        return;
      }
    } catch (_) {}

    try {
      window.location.replace('/');
    } catch (_) {
      window.location.href = '/';
    }
  }
};

/**
 * Extracts filename from Content-Disposition header
 * @param contentDisposition - The Content-Disposition header value
 * @param fallbackFilename - The fallback filename to use if extraction fails
 * @returns The extracted filename or fallback filename
 */
export const getFilenameFromHeader = (
  contentDisposition: string | null | undefined,
  fallbackFilename: string,
): string => {
  if (!contentDisposition) {
    return fallbackFilename;
  }

  // Try to extract filename from Content-Disposition header
  // Format: attachment; filename="filename.pdf"
  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1];
  }

  return fallbackFilename;
};

export const isHebrew = (string) => {
  return !!string?.split('').some((x) => x.match(/[\u0590-\u05FF]/));
};

export const hasLatin = (string?: string | null) =>
  /[A-Za-z]/.test(string || '');

/**
 * BiDi direction for the *text* (not the chip block).
 * Pure Hebrew → rtl. Mixed → auto so first-strong matches monday
 * (Hebrew-first → ✓ Signed חתום). Put this on an inner isolate; keep
 * toolbar Align on the outer via resolveTextAlign / text-align.
 */
export const resolveTextDirection = (
  string?: string | null,
): 'ltr' | 'rtl' | 'auto' => {
  if (!string || !isHebrew(string)) return 'ltr';
  if (hasLatin(string)) return 'auto';
  return 'rtl';
};

/** Toolbar Align Left/Center/Right → CSS text-align (default left). */
export const resolveTextAlign = (
  alignment?: string | null,
): 'left' | 'center' | 'right' => {
  if (alignment === 'center' || alignment === 'right') return alignment;
  return 'left';
};

/** Inner span style: BiDi isolate so dir=auto does not hug the chip to the right. */
export const BIDI_ISOLATE_STYLE: { unicodeBidi: 'isolate' } = {
  unicodeBidi: 'isolate',
};

export const noop = (): void => {};
